<template>
  <Dialog :commonDialog="budgetDialog" :dialogWidth="dialogWidth">
    <template v-slot:title>
      {{ isEdit ? "Edit Budget" : "Add Budget" }}
    </template>

    <template v-slot:body>
      <v-form
        ref="budgetForm"
        v-model.trim="formValid"
        lazy-validation
        @submit.stop.prevent="submitBudget"
      >
        <v-container fluid class="py-0">
          <!-- Cost Category -->
          <v-row>
            <v-col md="12">
              <v-btn-toggle
                v-model="budgetData.cost_type"
                mandatory
                borderless
                class="my-0 w-100 claim-tabs"
                min-height="30"
              >
                <v-btn
                  value="internal"
                  class="flex-grow-1 mr-2"
                  depressed
                  :style="
                    budgetData.cost_type == 'internal'
                      ? { color: '#ffff', background: 'green' }
                      : ''
                  "
                  min-height="40"
                >
                  Internal Cost
                </v-btn>
                <v-btn
                  value="external"
                  class="flex-grow-1 border-0"
                  depressed
                  :style="
                    budgetData.cost_type == 'external'
                      ? { color: '#ffff', background: 'orange' }
                      : ''
                  "
                  min-height="40"
                >
                  External Cost
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6">
              <label class="font-weight-500 font-size-16 required"
                >Cost Category</label
              >
              <v-select
                :menu-props="{ offsetY: true }"
                v-model.trim="budgetData.cost_category"
                :items="costCategories"
                :disabled="pageLoading"
                :rules="[
                  validateRules.required(
                    budgetData.cost_category,
                    'Cost Category'
                  ),
                ]"
                dense
                filled
                flat
                solo
                item-text="text"
                item-value="value"
                append-outer-icon="mdi-cog"
                @click:append-outer="openCostCategoryDialog"
                hide-details
                placeholder="Select Cost Category"
                color="cyan"
                item-color="cyan"
              />
            </v-col>

            <v-col md="6" v-if="budgetData.cost_type === 'internal'">
              <label class="font-weight-500 font-size-16 required">SBU</label>
              <v-select
                :menu-props="{ offsetY: true }"
                v-model.trim="budgetData.sbu"
                :items="sbuList"
                :disabled="pageLoading"
                :rules="[validateRules.required(budgetData.sbu, 'SBU')]"
                dense
                filled
                flat
                solo
                item-text="text"
                item-value="value"
                append-outer-icon="mdi-cog"
                @click:append-outer="openSbuDialog"
                hide-details
                placeholder="Select SBU"
                color="cyan"
                item-color="cyan"
              />
            </v-col>
            <v-col v-else md="6">
              <label>External Vendor</label>
              <v-combobox
                v-model.trim="budgetData.external_vendor"
                :items="supplierList"
                :loading="supplierLoading"
                :rules="[
                  validateRules.maxLength(
                    budgetData.external_vendor,
                    'external vendor',
                    100
                  ),
                ]"
                dense
                filled
                flat
                :maxLength="100"
                :counter="100"
                solo
                hide-details
                placeholder="Select or enter supplier name"
                clearable
                no-data-text="Supplier not found"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6">
              <label>Rate</label>
              <v-text-field
                v-model.number="budgetData.rate"
                dense
                filled
                flat
                solo
                hide-details
                type="number"
                step="0.01"
                placeholder="0.00"
                @keypress="
                  limitMaxLength($event, budgetData.rate, 9);
                  limitDecimal($event, budgetData.rate);
                "
              />
            </v-col>

            <v-col md="6">
              <label>Unit</label>
              <v-select
                v-model.trim="budgetData.unit"
                :items="unitsList"
                :disabled="pageLoading"
                dense
                filled
                flat
                solo
                item-text="text"
                item-value="value"
                hide-details
                placeholder="Select Unit"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6">
              <label>Quantity</label>
              <v-text-field
                v-model.number="budgetData.quantity"
                dense
                filled
                flat
                solo
                hide-details
                type="number"
                step="0.01"
                placeholder="0.00"
                @keypress="
                  limitMaxLength($event, budgetData.quantity, 9);
                  limitDecimal($event, budgetData.quantity);
                "
              />
            </v-col>

            <v-col md="6">
              <label>Amount</label>
              <v-text-field
                v-model.number="budgetData.amount"
                dense
                filled
                flat
                solo
                hide-details
                type="number"
                step="0.01"
                placeholder="0.00"
                @keypress="
                  limitMaxLength($event, budgetData.amount, 9);
                  limitDecimal($event, budgetData.amount);
                "
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6">
              <label>Description</label>
              <TextAreaField
                auto-grow
                dense
                filled
                color="cyan"
                v-model="budgetData.description"
                placeholder="Enter Description.."
                solo
                flat
                row-height="25"
                counter="250"
              />
            </v-col>
            <v-col md="6">
              <label>Remarks</label>
              <TextAreaField
                auto-grow
                dense
                filled
                color="cyan"
                v-model="budgetData.remarks"
                placeholder="Enter Remarks.."
                solo
                flat
                row-height="25"
                counter="250"
              />
            </v-col>
          </v-row>
        </v-container>

        <!-- Cost Category Dialog -->
        <Manage-Motor-Settings
          :mDialog="manageCostCategoryDialog"
          :settings="costCategories"
          title="Cost Category"
          endpoint="setting/cost-category"
          value="cost_category"
          @close-dialog="manageCostCategoryDialog = false"
          @get-settings="getCostCategories"
        />

        <!-- SBU Dialog -->
        <Manage-Motor-Settings
          :mDialog="manageSbuDialog"
          :settings="sbuList"
          title="SBU"
          endpoint="setting/sbu"
          value="sbu"
          @close-dialog="manageSbuDialog = false"
          @get-settings="getSbuList"
        />
      </v-form>
    </template>

    <template v-slot:action>
      <v-tooltip bottom content-class="custom-bottom-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            depressed
            :disabled="pageLoading || !formValid"
            :loading="pageLoading"
            color="cyan"
            class="mx-2 custom-bold-button white--text"
            v-bind="attrs"
            v-on="on"
            @click="submitBudget"
          >
            {{ isEdit ? "Update" : "Save" }}
          </v-btn>
        </template>
        <span>Click here to {{ isEdit ? "update" : "save" }} budget</span>
      </v-tooltip>

      <v-btn depressed class="mx-2" @click="closeDialog">Close</v-btn>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import ManageMotorSettings from "@/view/pages/partials/Manage-Motor-Settings.vue";
import ValidationMixin from "@/core/plugins/validation-mixin";
import CommonMixin from "@/core/plugins/common-mixin";
import { GET, POST, PUT } from "@/core/services/store/request.module";

export default {
  mixins: [ValidationMixin, CommonMixin],
  props: {
    budgetDialog: {
      type: Boolean,
      default: false,
    },
    quotationId: Number,
    editData: Object,
  },

  data() {
    return {
      formValid: true,
      pageLoading: false,
      manageCostCategoryDialog: false,
      manageSbuDialog: false,
      costCategories: [],
      unitsList: [],
      // fixedStatusList: [
      //   { text: 'Pending', value: 1 },
      //   { text: 'Accepted', value: 2 },
      //   { text: 'Rejected', value: 3 }
      // ],
      sbuList: [],
      supplierList: [],
      supplierLoading: false,
      budgetData: {
        cost_category: null,
        sbu: null,
        external_vendor: null,
        cost_type: "internal",
        description: null,
        rate: null,
        unit: null,
        quantity: null,
        amount: null,
        status: 1,
        remarks: null,
      },
    };
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 50);
    },
    isEdit() {
      return !!this.editData;
    },
  },
  watch: {
    budgetDialog(val) {
      if (val) {
        this.initData();
        this.getCostCategories();
        this.getSbuList();
        this.getUnits();
        this.getSuppliers();
      }
    },
  },
  methods: {
    openCostCategoryDialog() {
      if (console) console.log("Gear clicked for cost category");
      this.manageCostCategoryDialog = true;
    },
    openSbuDialog() {
      if (console) console.log("Gear clicked for SBU");
      this.manageSbuDialog = true;
    },
    initData() {
      if (this.editData) {
        this.budgetData = {
          id: this.editData.id,
          cost_category: this.editData.cost_category_name || null,
          sbu: this.editData.sbu_name || null,
          external_vendor: this.editData.external_vendor || null,
          cost_type: this.editData.cost_type || "internal",
          description: this.editData.description || null,
          rate: this.editData.rate || null,
          unit: this.editData.unit || null,
          quantity:
            this.editData.quantity !== undefined
              ? this.editData.quantity
              : null,
          amount:
            this.editData.amount !== undefined ? this.editData.amount : null,
          status: this.editData.status_name || null,
          remarks: this.editData.remarks || null,
        };
        this.setPreselected();
      } else {
        this.resetBudgetData();
      }
    },
    resetBudgetData() {
      this.budgetData = {
        id: null,
        cost_category: null,
        sbu: null,
        external_vendor: null,
        cost_type: "internal",
        description: null,
        rate: null,
        unit: null,
        quantity: null,
        amount: null,
        status: 1,
        remarks: null,
      };
    },
    getCostCategories() {
      this.$store
        .dispatch(GET, { url: "setting/cost-category" })
        .then(({ data }) => {
          this.costCategories = data.filter((item) => item.activated);
          this.setPreselected();
        });
    },
    getSbuList() {
      this.$store.dispatch(GET, { url: "setting/sbu" }).then(({ data }) => {
        this.sbuList = data.filter((item) => item.activated);
        this.setPreselected();
      });
    },

    getUnits() {
      this.$store.dispatch(GET, { url: "setting/units" }).then(({ data }) => {
        this.unitsList = data.filter((item) => item.activated);
        this.setPreselected();
      });
    },
    getSuppliers() {
      this.supplierLoading = true;
      console.log("Fetching suppliers...");
      this.$store
        .dispatch(GET, { url: "supplier?status=active" })
        .then(({ data }) => {
          console.log("Suppliers API response:", data);
          if (data && data.rows) {
            this.supplierList = data.rows.map(
              (supplier) => supplier.display_name
            );
            console.log("Processed supplier list:", this.supplierList);
          } else if (data && Array.isArray(data)) {
            this.supplierList = data.map((supplier) => supplier.display_name);
            console.log("Processed supplier list (array):", this.supplierList);
          } else {
            console.log("No supplier data found");
            this.supplierList = [];
          }
        })
        .catch((error) => {
          console.error("Error fetching suppliers:", error);
          this.logError(error);
        })
        .finally(() => {
          this.supplierLoading = false;
        });
    },
    setPreselected() {
      if (
        this.editData &&
        this.costCategories.length &&
        this.sbuList.length &&
        this.unitsList.length
      ) {
        const costCategory = this.costCategories.find(
          (item) => item.text === this.budgetData.cost_category
        );
        if (costCategory) {
          this.budgetData.cost_category = costCategory.value;
        }
        const sbu = this.sbuList.find(
          (item) => item.text === this.budgetData.sbu
        );
        if (sbu) {
          this.budgetData.sbu = sbu.value;
        }
        const unit = this.unitsList.find(
          (item) =>
            item.text === this.budgetData.unit ||
            item.value == this.budgetData.unit
        );
        if (unit) {
          this.budgetData.unit = unit.value;
        }
        if (this.budgetData.status) {
          const statusText = this.budgetData.status.toString().toLowerCase();
          if (statusText === "pending") {
            this.budgetData.status = 1;
          } else if (statusText === "accepted") {
            this.budgetData.status = 2;
          } else if (statusText === "rejected") {
            this.budgetData.status = 3;
          }
        }
      }
    },
    submitBudget() {
      if (!this.$refs.budgetForm.validate()) return;

      this.pageLoading = true;

      const requestData = {
        ...this.budgetData,
        quotation_id: this.quotationId,
      };

      const method = this.isEdit ? PUT : POST;
      const url = this.isEdit
        ? `quotation/${this.quotationId}/budget/${this.budgetData.id}`
        : `quotation/${this.quotationId}/budget`;

      this.$store
        .dispatch(method, {
          url,
          data: requestData,
        })
        .then(() => {
          this.$emit("success");
          this.closeDialog();
        })
        .finally(() => (this.pageLoading = false));
    },
    closeDialog() {
      this.$emit("close");
      this.resetBudgetData();
      this.$refs.budgetForm.reset();
    },
  },
  components: { Dialog, ManageMotorSettings },
};
</script>

<style lang="scss" scoped>
label {
  padding-left: 5px;
  font-weight: 600;
  font-family: "Poppins", Helvetica, Arial, sans-serif;
}

p {
  padding-left: 5px;
}
</style>
